import React, { useState } from 'react';
import { Mail, Sparkles, Check, AlertCircle, Loader2 } from 'lucide-react';
import api from '../api';
import { Button } from './ui/button';
import { Input } from './ui/input';

/**
 * NewsletterForm — formulaire d'inscription opt-in à la newsletter.
 *
 * Variantes visuelles :
 *   variant="card"    → carte autonome (utilisable en page d'accueil)
 *   variant="footer"  → version sobre, intégrable dans le footer
 *   variant="inline"  → sans wrapping (parent gère le style)
 *
 * Conformité RGPD :
 *   · Consentement explicite (checkbox cochée par défaut visible mais l'envoi
 *     reste opt-in via l'action utilisateur)
 *   · Mention claire du droit de désinscription
 *   · Pas de tracking publicitaire
 */
export function NewsletterForm({
  variant = 'card',
  source = 'home',
  title = 'Reste informé des nouveautés',
  subtitle = 'Reçois en avant-première les nouvelles éditions hebdo, modules et fonctionnalités.',
}) {
  const [email, setEmail] = useState('');
  const [consent, setConsent] = useState(true);
  const [status, setStatus] = useState('idle'); // idle | loading | success | error
  const [message, setMessage] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      setStatus('error');
      setMessage('Merci de renseigner ton email');
      return;
    }
    if (!consent) {
      setStatus('error');
      setMessage('Le consentement est requis pour valider l\'inscription');
      return;
    }
    setStatus('loading');
    setMessage('');
    try {
      const res = await api.newsletterSubscribe(email.trim().toLowerCase(), source);
      setStatus('success');
      setMessage(res?.message || 'Bienvenue !');
      setEmail('');
    } catch (err) {
      setStatus('error');
      setMessage(err?.message || "Une erreur est survenue. Réessaie plus tard.");
    }
  };

  const isInline = variant === 'inline';
  const isFooter = variant === 'footer';

  return (
    <div
      className={
        isFooter
          ? 'space-y-3'
          : isInline
            ? ''
            : 'mc-card mc-card-accent-official p-5 sm:p-6 space-y-4'
      }
      data-testid={`newsletter-form-${variant}`}
    >
      {!isInline && (
        <div className={isFooter ? '' : 'flex items-start gap-3'}>
          {!isFooter && (
            <div className="w-10 h-10 rounded-2xl bg-[hsl(var(--primary-soft))] flex items-center justify-center flex-shrink-0">
              <Mail className="w-5 h-5 text-[hsl(var(--primary))]" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <h3 className={`font-black tracking-tight ${isFooter ? 'text-base' : 'text-lg'}`}>
              {title}
            </h3>
            {subtitle && (
              <p className="text-sm text-foreground/70 mt-1 leading-relaxed">{subtitle}</p>
            )}
          </div>
        </div>
      )}

      <form onSubmit={submit} className="space-y-3">
        <div className="flex flex-col sm:flex-row gap-2">
          <Input
            type="email"
            inputMode="email"
            placeholder="ton.email@exemple.fr"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={status === 'loading' || status === 'success'}
            className="flex-1"
            data-testid={`newsletter-email-input-${variant}`}
            required
          />
          <Button
            type="submit"
            disabled={status === 'loading' || status === 'success'}
            className="mc-btn-primary gap-1.5 whitespace-nowrap"
            data-testid={`newsletter-submit-${variant}`}
          >
            {status === 'loading' ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> Envoi…</>
            ) : status === 'success' ? (
              <><Check className="w-4 h-4" /> Inscrit !</>
            ) : (
              <><Sparkles className="w-4 h-4" /> M'inscrire</>
            )}
          </Button>
        </div>

        <label className="flex items-start gap-2 text-xs text-foreground/65 cursor-pointer leading-snug">
          <input
            type="checkbox"
            checked={consent}
            onChange={(e) => setConsent(e.target.checked)}
            className="mt-0.5 accent-[hsl(var(--primary))]"
            data-testid={`newsletter-consent-${variant}`}
          />
          <span>
            J'accepte de recevoir par email les nouveautés Code 2 Roues.
            Désinscription à tout moment d'un simple clic. Aucune donnée transmise à un tiers.
          </span>
        </label>

        {message && (
          <div
            className={`flex items-start gap-2 text-sm rounded-md px-3 py-2 ${
              status === 'success'
                ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/30'
                : 'bg-destructive/10 text-destructive border border-destructive/30'
            }`}
            role="status"
            data-testid={`newsletter-message-${variant}`}
          >
            {status === 'success' ? (
              <Check className="w-4 h-4 mt-0.5 flex-shrink-0" />
            ) : (
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            )}
            <span>{message}</span>
          </div>
        )}
      </form>
    </div>
  );
}

export default NewsletterForm;
